'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SearchBarProps {
  variant?: 'full' | 'condensed';
  onSearch?: (filters: SearchFilters) => void;
  className?: string;
}

interface SearchFilters {
  brand: string;
  location: string;
  service: string;
}

const SearchBar = ({ variant = 'full', onSearch, className = '' }: SearchBarProps) => {
  const [filters, setFilters] = useState<SearchFilters>({
    brand: '',
    location: '',
    service: ''
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [isGeoLoading, setIsGeoLoading] = useState(false);

  useEffect(() => {
    // Check if URL has location
    const urlParams = new URLSearchParams(window.location.search);
    const urlLocation = urlParams.get('location');
    const urlBrand = urlParams.get('brand') || '';
    const urlService = urlParams.get('service') || '';

    if (urlLocation) {
        setFilters({ brand: urlBrand, location: urlLocation, service: urlService });
        return;
    }

    // Otherwise, fetch GeoIP
    const detectLocation = async () => {
        setIsGeoLoading(true);
        try {
            const res = await fetch('/api/geo');
            if (res.ok) {
                const data = await res.json();
                if (data.city && data.region) {
                    setFilters(prev => ({
                        ...prev,
                        location: `${data.city}, ${data.region}`
                    }));
                }
            }
        } catch (e) {
            console.error('Failed to parse geo location', e);
        } finally {
            setIsGeoLoading(false);
        }
    };

    detectLocation();
  }, []);

  const brands = ['BMW', 'Lexus', 'Mercedes-Benz', 'Audi', 'Acura', 'Infiniti', 'Toyota', 'Honda'];
  const services = [
    'Oil Change',
    'Brake Service',
    'Engine Diagnostics',
    'Transmission Repair',
    'Suspension Work',
    'Electrical Systems',
    'Body Shop'
  ];

  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch(filters);
    }
  };

  const handleClear = () => {
    setFilters({
      brand: '',
      location: '',
      service: ''
    });
  };

  if (variant === 'condensed') {
    return (
      <div className={`bg-card rounded-lg shadow-elevation-sm ${className}`}>
        <div className="flex items-center space-x-2 p-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-2 flex-1 rounded-md border border-border bg-input px-4 py-2 text-left text-sm text-text-secondary transition-smooth hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <Icon name="MagnifyingGlassIcon" size={18} />
            <span className="truncate">
              {filters.brand || filters.service || filters.location || 'Refine search...'}
            </span>
          </button>
          <button
            onClick={handleSearch}
            className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground transition-smooth hover:opacity-90 active:scale-[0.97]"
            aria-label="Search"
          >
            <Icon name="MagnifyingGlassIcon" size={20} />
          </button>
        </div>

        {isExpanded && (
          <div className="border-t border-border p-4 space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="font-caption text-xs font-medium text-text-secondary mb-1 block">
                  Brand
                </label>
                <select
                  value={filters.brand}
                  onChange={(e) => handleFilterChange('brand', e.target.value)}
                  className="w-full rounded-md border border-border bg-input px-3 py-2 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <option value="">All Brands</option>
                  {brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-caption text-xs font-medium text-text-secondary mb-1 block">
                  Location
                </label>
                <input
                  type="text"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  placeholder="City or ZIP"
                  className="w-full rounded-md border border-border bg-input px-3 py-2 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
              </div>

              <div>
                <label className="font-caption text-xs font-medium text-text-secondary mb-1 block">
                  Service
                </label>
                <select
                  value={filters.service}
                  onChange={(e) => handleFilterChange('service', e.target.value)}
                  className="w-full rounded-md border border-border bg-input px-3 py-2 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <option value="">All Services</option>
                  {services.map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-2">
              <button
                onClick={handleClear}
                className="rounded-md px-4 py-2 text-sm font-medium text-text-secondary transition-smooth hover:bg-muted hover:text-text-primary"
              >
                Clear
              </button>
              <button
                onClick={handleSearch}
                className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground transition-smooth hover:opacity-90 active:scale-[0.97]"
              >
                Update Search
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-xl shadow-elevation-lg p-6 sm:p-8 ${className}`}>
      <div className="space-y-6">
        <div className="text-center sm:text-left">
          <h2 className="font-heading text-2xl font-semibold text-text-primary mb-2">
            Find Your Trusted Mechanic
          </h2>
          <p className="font-body text-sm text-text-secondary">
            Search by brand, location, and service type
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="brand" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
              Vehicle Brand
            </label>
            <div className="relative">
              <Icon 
                name="TruckIcon" 
                size={18} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
              />
              <select
                id="brand"
                value={filters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <option value="">Select Brand</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="location" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
              Location
            </label>
            <div className="relative">
              <Icon 
                name="MapPinIcon" 
                size={18} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
              />
              <input
                id="location"
                type="text"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                placeholder="City or ZIP code"
                className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
            </div>
          </div>

          <div>
            <label htmlFor="service" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
              Service Type
            </label>
            <div className="relative">
              <Icon 
                name="WrenchScrewdriverIcon" 
                size={18} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
              />
              <select
                id="service"
                value={filters.service}
                onChange={(e) => handleFilterChange('service', e.target.value)}
                className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <option value="">Select Service</option>
                {services.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <button
            onClick={handleClear}
            className="flex items-center justify-center space-x-2 rounded-lg border border-border px-6 py-3 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:text-primary active:scale-[0.97]"
          >
            <Icon name="XMarkIcon" size={18} />
            <span>Clear Filters</span>
          </button>

          <button
            onClick={handleSearch}
            className="flex items-center justify-center space-x-2 rounded-lg bg-primary px-8 py-3 font-body text-sm font-medium text-primary-foreground shadow-elevation-sm transition-smooth hover:opacity-90 active:scale-[0.97]"
          >
            <Icon name="MagnifyingGlassIcon" size={18} />
            <span>Search Mechanics</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;